import { useEffect } from 'react';

const SITE_URL = 'https://www.autonewsweekly.com';
const SITE_NAME = 'Auto News Weekly';
const LOGO_URL = `${SITE_URL}/favicon.png`;

// Organization + Website schema (site-wide)
export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: LOGO_URL,
          width: 512,
          height: 512,
        },
        sameAs: [
          'https://twitter.com/AutoNewsWeekly',
          'https://facebook.com/AutoNewsWeekly',
          'https://linkedin.com/company/autonewsweekly',
          'https://youtube.com/@AutoNewsWeekly',
        ],
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: SITE_NAME,
        publisher: { '@id': `${SITE_URL}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_URL}/news?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };

  return <JsonLd data={schema} />;
}

// Breadcrumb schema
interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbSchema({ items }: { items: BreadcrumbItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return <JsonLd data={schema} />;
}

// Article/NewsArticle schema
interface ArticleSchemaProps {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  author: string;
  url: string;
  section?: string;
}

export function ArticleSchema({
  title,
  description,
  image,
  datePublished,
  dateModified,
  author,
  url,
  section,
}: ArticleSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: title,
    description: description,
    image: [image],
    datePublished: datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': 'Person',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    ...(section && { articleSection: section }),
  };

  return <JsonLd data={schema} />;
}

// Review schema
interface ReviewSchemaProps {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  author: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number;
  ratingValue?: number;
  url: string;
}

export function ReviewSchema({
  title,
  description,
  image,
  datePublished,
  author,
  vehicleMake,
  vehicleModel,
  vehicleYear,
  ratingValue = 4,
  url,
}: ReviewSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    headline: title,
    description: description,
    image: image,
    datePublished: datePublished,
    author: {
      '@type': 'Person',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: LOGO_URL },
    },
    itemReviewed: {
      '@type': 'Vehicle',
      name: `${vehicleYear} ${vehicleMake} ${vehicleModel}`,
      brand: { '@type': 'Brand', name: vehicleMake },
      model: vehicleModel,
      vehicleModelDate: vehicleYear.toString(),
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: ratingValue,
      bestRating: 5,
      worstRating: 1,
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  };

  return <JsonLd data={schema} />;
}

// Video schema
interface VideoSchemaProps {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string; // ISO 8601 duration format (e.g., "PT5M30S")
  embedUrl?: string;
  contentUrl?: string;
}

export function VideoSchema({
  name,
  description,
  thumbnailUrl,
  uploadDate,
  duration,
  embedUrl,
  contentUrl,
}: VideoSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: name,
    description: description,
    thumbnailUrl: thumbnailUrl,
    uploadDate: uploadDate,
    ...(duration && { duration }),
    ...(embedUrl && { embedUrl }),
    ...(contentUrl && { contentUrl }),
    potentialAction: {
      '@type': 'WatchAction',
      target: embedUrl || contentUrl,
    },
  };

  return <JsonLd data={schema} />;
}

// Event schema
interface EventSchemaProps {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  location?: string;
  isVirtual?: boolean;
  image?: string;
  url: string;
  organizer?: string;
}

export function EventSchema({
  name,
  description,
  startDate,
  endDate,
  location,
  isVirtual = false,
  image,
  url,
  organizer,
}: EventSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: name,
    description: description,
    startDate: startDate,
    ...(endDate && { endDate }),
    location: isVirtual
      ? { '@type': 'VirtualLocation', url: url }
      : { '@type': 'Place', name: location || 'TBA', address: location },
    ...(image && { image: image }),
    url: url,
    organizer: {
      '@type': 'Organization',
      name: organizer || SITE_NAME,
    },
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: isVirtual
      ? 'https://schema.org/OnlineEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode',
  };

  return <JsonLd data={schema} />;
}

// FAQ Schema
interface FAQItem {
  question: string;
  answer: string;
}

export function FAQSchema({ items }: { items: FAQItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return <JsonLd data={schema} />;
}

// Generic JSON-LD component
function JsonLd({ data }: { data: object }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    script.setAttribute('data-seo', 'true');
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [data]);

  return null;
}

export { JsonLd };
